import { Link, useLocation } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth-context";
import { useTenant } from "../lib/tenant-context";

interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale"; // API returns 'type' not 'program_type'
  status: string;
}

interface ProgramsByType {
  takeback: Program[];
  trade_in: Program[];
  resale: Program[];
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { currentTenant, setTenantId, availableTenants } = useTenant();
  const location = useLocation();
  const [programs, setPrograms] = useState<ProgramsByType>({
    takeback: [],
    trade_in: [],
    resale: [],
  });
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["takeback", "resale"]),
  );
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);

  // Use tenant from context instead of local state
  const selectedTenantId = currentTenant.id;
  const selectedTenant = currentTenant;

  const handleTenantChange = (tenantId: string) => {
    setTenantId(tenantId);
    setIsTenantDropdownOpen(false);
    // Dispatch custom event for components to listen
    window.dispatchEvent(
      new CustomEvent("tenantChanged", { detail: { tenantId } }),
    );
    // Reload page to reflect tenant change
    window.location.reload();
  };

  // Fetch programs on mount and when tenant changes
  useEffect(() => {
    fetch(`/api/programs?tenantId=${encodeURIComponent(selectedTenantId)}`)
      .then((res) => res.json())
      .then((data) => {
        const byType: ProgramsByType = {
          takeback: [],
          trade_in: [],
          resale: [],
        };
        (data.programs || []).forEach((p: Program) => {
          if (byType[p.type]) {
            byType[p.type].push(p);
          }
        });
        setPrograms(byType);
      })
      .catch(() => {});
  }, [selectedTenantId]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const isActive = (href: string) => {
    return (
      location.pathname === href ||
      (href !== "/" && location.pathname.startsWith(href))
    );
  };

  const isProgramActive = (programId: string) => {
    return location.pathname.includes(programId);
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#FAFAFA" }}>
      {/* Sidebar */}
      <aside
        className="w-64 border-r flex flex-col fixed h-screen"
        style={{
          backgroundColor: "#FFFFFF",
          borderColor: "rgba(169,169,169,0.2)",
        }}
      >
        {/* Logo */}
        <div
          className="h-16 px-6 flex items-center border-b"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <Link to="/" className="flex items-center group">
            <img
              src="/images/pentatonic-logo.svg"
              alt="Pentatonic"
              className="h-5"
              style={{ width: "auto" }}
            />
          </Link>
        </div>

        {/* Tenant Selector */}
        <div
          className="px-4 py-4 border-b relative"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <button
            onClick={() =>
              availableTenants.length > 1 &&
              setIsTenantDropdownOpen(!isTenantDropdownOpen)
            }
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
              availableTenants.length > 1
                ? "hover:bg-gray-50 cursor-pointer"
                : "cursor-default"
            }`}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: selectedTenant.color }}
            >
              {selectedTenant.id === "nike" ? (
                <img
                  src="/images/nike/swoosh-white.svg"
                  alt="Nike"
                  className="w-5 h-5 object-contain"
                />
              ) : (
                <span className="font-medium text-sm text-white">
                  {selectedTenant.initial}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate" style={{ color: "#171717" }}>
                {selectedTenant.name}
              </p>
              <p className="text-xs font-mono" style={{ color: "#A9A9A9" }}>
                {selectedTenant.plan}
              </p>
            </div>
            {/* Only show dropdown arrow if user can switch tenants */}
            {availableTenants.length > 1 && (
              <ChevronDownIcon
                className={`transition-transform ${isTenantDropdownOpen ? "rotate-180" : ""}`}
              />
            )}
          </button>

          {/* Dropdown Menu - only show if user can access multiple tenants */}
          {isTenantDropdownOpen && availableTenants.length > 0 && (
            <div
              className="absolute top-full left-4 right-4 mt-2 bg-white border rounded-lg shadow-lg z-50"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              {availableTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantChange(tenant.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left first:rounded-t-lg last:rounded-b-lg ${
                    selectedTenantId === tenant.id ? "bg-gray-50" : ""
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: tenant.color }}
                  >
                    {tenant.id === "nike" ? (
                      <img
                        src="/images/nike/swoosh-white.svg"
                        alt="Nike"
                        className="w-5 h-5 object-contain"
                      />
                    ) : (
                      <span className="font-medium text-sm text-white">
                        {tenant.initial}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm truncate"
                      style={{ color: "#171717" }}
                    >
                      {tenant.name}
                    </p>
                    <p
                      className="text-xs font-mono"
                      style={{ color: "#A9A9A9" }}
                    >
                      {tenant.plan}
                    </p>
                  </div>
                  {selectedTenantId === tenant.id && (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {selectedTenantId === "arcteryx" ? (
            <>
              {/* REBIRD Section */}
              <div className="mb-6">
                <p
                  className="px-3 py-2 text-xs font-medium tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  REBIRD
                </p>
                <NavItem
                  href="/shopify-trade-ins"
                  icon={<ShopifyIcon />}
                  label="Trade-ins"
                  isActive={isActive("/shopify-trade-ins")}
                />
                <NavItem
                  href="/inventory"
                  icon={<InventoryIcon />}
                  label="Resale Inventory"
                  isActive={isActive("/inventory")}
                />
                <NavItem
                  href="/trade-in-analytics"
                  icon={<TradeInReportsIcon />}
                  label="Trade-In Analytics"
                  isActive={isActive("/trade-in-analytics")}
                />
                <NavItem
                  href="/customers"
                  icon={<CustomersIcon />}
                  label="Customers"
                  isActive={isActive("/customers")}
                />
                <ComingSoonNavItem icon={<WashRepairIcon />} label="Wash & Repair" />
              </div>

              {/* UNDERSTAND Section */}
              <div className="mb-6">
                <p
                  className="px-3 py-2 text-xs font-medium tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  UNDERSTAND
                </p>
                <NavItem
                  href="/"
                  icon={<HomeIcon />}
                  label="Overview"
                  isActive={location.pathname === "/"}
                />
                <NavItem
                  href="/products"
                  icon={<ProductsIcon />}
                  label="Products"
                  isActive={isActive("/products")}
                />
                <NavItem
                  href="/knowledge"
                  icon={<KnowledgeIcon />}
                  label="Knowledge Hub"
                  isActive={isActive("/knowledge")}
                />
                <NavItem
                  href="/market-intelligence"
                  icon={<MarketIntelIcon />}
                  label="Market Intelligence"
                  isActive={isActive("/market-intelligence")}
                />
                <NavItem
                  href="/conversational-analytics"
                  icon={<ChatIcon />}
                  label="Conversational Analytics"
                  isActive={isActive("/conversational-analytics")}
                />
                <NavItem
                  href="/pricing-engine"
                  icon={<PricingIcon />}
                  label="Pricing Engine"
                  isActive={isActive("/pricing-engine")}
                />
                <NavItem
                  href="/stores"
                  icon={<StoreIcon />}
                  label="Stores"
                  isActive={isActive("/stores")}
                />
              </div>

              {/* BUILD Section */}
              <div className="mb-6">
                <p
                  className="px-3 py-2 text-xs font-medium tracking-wider"
                  style={{ color: "#A9A9A9" }}
                >
                  BUILD
                </p>
                <NavItem
                  href="/api-keys"
                  icon={<KeyIcon />}
                  label="API Keys"
                  isActive={isActive("/api-keys")}
                />
                <NavItem
                  href="/docs/getting-started"
                  icon={<DocsIcon />}
                  label="Documentation"
                  isActive={isActive("/docs/getting-started")}
                />
              </div>

            </>
          ) : (
            <>
          {/* UNDERSTAND Section */}
          <div className="mb-6">
            <p
              className="px-3 py-2 text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              UNDERSTAND
            </p>
            <NavItem
              href="/"
              icon={<HomeIcon />}
              label="Overview"
              isActive={location.pathname === "/"}
            />
            <NavItem
              href="/products"
              icon={<ProductsIcon />}
              label="Products"
              isActive={isActive("/products")}
            />
            <NavItem
              href="/knowledge"
              icon={<KnowledgeIcon />}
              label="Knowledge Hub"
              isActive={isActive("/knowledge")}
            />
            <NavItem
              href="/docs"
              icon={<IntelligenceIcon />}
              label="Intelligence APIs"
              isActive={isActive("/docs")}
            />
            <NavItem
              href="/market-intelligence"
              icon={<MarketIntelIcon />}
              label="Market Intelligence"
              isActive={isActive("/market-intelligence")}
            />
          </div>

          {/* MANAGE Section - Dynamic based on programs */}
          <div className="mb-6">
            <p
              className="px-3 py-2 text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              MANAGE
            </p>

            {/* Physical Intelligence */}
            <NavItem
              href="/pin"
              icon={<KioskIcon />}
              label="Physical Intelligence"
              isActive={isActive("/pin")}
            />

            {/* Shopify Trade-Ins */}
            <NavItem
              href="/shopify-trade-ins"
              icon={<ShopifyIcon />}
              label="Shopify Trade-Ins"
              isActive={isActive("/shopify-trade-ins")}
            />

            {/* Nespresso Recycling Reports - only show for Nespresso tenant */}
            {selectedTenantId === "nespresso" && (
              <NavItem
                href="/nespresso-analytics"
                icon={<RecyclingIcon />}
                label="Recycling Reports"
                isActive={isActive("/nespresso-analytics")}
              />
            )}

            {/* Warehouse - show for tenants with warehouse access */}
            {(selectedTenantId === "nespresso" ||
              selectedTenantId === "adidas") && (
              <NavItem
                href="/warehouse"
                icon={<WarehouseIcon />}
                label="Warehouse"
                isActive={isActive("/warehouse")}
              />
            )}

            {/* Takeback Programs */}
            {programs.takeback.length > 0 && (
              <ProgramSection
                label="Takeback"
                icon={<TakebackIcon />}
                programs={programs.takeback}
                expanded={expandedSections.has("takeback")}
                onToggle={() => toggleSection("takeback")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Takebacks", path: "/takebacks" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Trade-in Programs */}
            {programs.trade_in.length > 0 && (
              <ProgramSection
                label="Trade-in"
                icon={<TradeInIcon />}
                programs={programs.trade_in}
                expanded={expandedSections.has("trade_in")}
                onToggle={() => toggleSection("trade_in")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Trade-ins", path: "/takebacks" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Resale Programs */}
            {programs.resale.length > 0 && (
              <ProgramSection
                label="Resale"
                icon={<ResaleIcon />}
                programs={programs.resale}
                expanded={expandedSections.has("resale")}
                onToggle={() => toggleSection("resale")}
                isProgramActive={isProgramActive}
                currentPath={location.pathname}
                subPages={[
                  { label: "Dashboard", path: "/dashboard" },
                  { label: "Listings", path: "" },
                  { label: "Analytics", path: "/analytics" },
                ]}
              />
            )}

            {/* Fallback if no programs */}
            {programs.takeback.length === 0 &&
              programs.trade_in.length === 0 &&
              programs.resale.length === 0 && (
                <Link
                  to="/programs"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{ color: "#A9A9A9" }}
                >
                  <PlusIcon />
                  <span>Create Program</span>
                </Link>
              )}
          </div>

          {/* CONFIGURE Section */}
          <div className="mb-6">
            <p
              className="px-3 py-2 text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              CONFIGURE
            </p>
            <NavItem
              href="/stores"
              icon={<StoreIcon />}
              label="Stores & Brands"
              isActive={isActive("/stores")}
            />
            <NavItem
              href="/programs"
              icon={<RouteIcon />}
              label="Programs"
              isActive={isActive("/programs") || isActive("/route-tester")}
            />
            <NavItem
              href="/treasury"
              icon={<TreasuryIcon />}
              label="Treasury"
              isActive={isActive("/treasury")}
            />
            <NavItem
              href="/x402-usage"
              icon={<X402Icon />}
              label="x402 Usage"
              isActive={isActive("/x402-usage")}
            />
            <NavItem
              href="/campaigns"
              icon={<CampaignsIcon />}
              label="Takeback Campaigns"
              isActive={isActive("/campaigns")}
            />
            <NavItem
              href="/sponsored"
              icon={<SponsoredIcon />}
              label="Sponsored Products"
              isActive={isActive("/sponsored")}
            />
          </div>

          {/* BUILD Section */}
          <div className="mb-6">
            <p
              className="px-3 py-2 text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              BUILD
            </p>
            <NavItem
              href="/api-keys"
              icon={<KeyIcon />}
              label="API Keys"
              isActive={isActive("/api-keys")}
            />
            <NavItem
              href="/docs/getting-started"
              icon={<DocsIcon />}
              label="Documentation"
              isActive={isActive("/docs/getting-started")}
            />
          </div>

          {/* Admin-only section */}
          {user?.isAdmin && (
            <div className="mb-6">
              <p
                className="px-3 py-2 text-xs font-medium tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                INTERNAL
              </p>
              <NavItem
                href="/revenue"
                icon={<RevenueIcon />}
                label="Revenue"
                isActive={isActive("/revenue")}
              />
            </div>
          )}
            </>
          )}
        </nav>

        {/* Footer */}
        <div
          className="px-4 py-4 border-t"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          {/* Settings Link */}
          <Link
            to="/settings"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors mb-3"
            style={
              isActive("/settings")
                ? { backgroundColor: "#171717", color: "#FFFFFF" }
                : { color: "#A9A9A9" }
            }
          >
            <span style={isActive("/settings") ? { color: "#00FBA9" } : {}}>
              <SettingsIcon />
            </span>
            Settings
          </Link>

          {user && (
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: currentTenant.color }}
              >
                <span className="text-sm text-white font-medium">
                  {user.tenantName[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate" style={{ color: "#171717" }}>
                  {user.tenantName}
                  {user.isAdmin && (
                    <span className="ml-1 text-xs" style={{ color: "#00FBA9" }}>
                      Admin
                    </span>
                  )}
                </p>
                <button
                  onClick={logout}
                  className="text-xs transition-colors hover:underline"
                  style={{ color: "#A9A9A9" }}
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-64">
        {/* Top bar */}
        <header
          className="h-16 border-b px-8 flex items-center justify-between sticky top-0 z-10"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.1)",
          }}
        >
          <div>{/* Breadcrumb or page title can go here */}</div>
          <div className="flex items-center gap-4">
            <Link
              to="/docs/getting-started"
              className="text-sm transition-colors"
              style={{ color: "#A9A9A9" }}
            >
              Help
            </Link>
          </div>
        </header>

        {/* Page content */}
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}

// Navigation Item Component
function NavItem({
  href,
  icon,
  label,
  isActive,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
      style={
        isActive
          ? { backgroundColor: "#171717", color: "#FFFFFF" }
          : { color: "#A9A9A9" }
      }
    >
      <span style={isActive ? { color: "#00FBA9" } : {}}>{icon}</span>
      {label}
    </Link>
  );
}

// Non-clickable "coming soon" nav item
function ComingSoonNavItem({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-default select-none"
      style={{ color: "rgba(169,169,169,0.45)" }}
      title="Coming soon"
    >
      <span>{icon}</span>
      <span className="flex-1">{label}</span>
      <span
        className="text-xs px-1.5 py-0.5 rounded"
        style={{
          backgroundColor: "rgba(169,169,169,0.12)",
          color: "rgba(169,169,169,0.6)",
          fontSize: "10px",
          letterSpacing: "0.05em",
        }}
      >
        SOON
      </span>
    </div>
  );
}

// Program Section with Collapsible Sub-navigation
function ProgramSection({
  label,
  icon,
  programs,
  expanded,
  onToggle,
  isProgramActive,
  subPages,
  currentPath,
}: {
  label: string;
  icon: React.ReactNode;
  programs: Program[];
  expanded: boolean;
  onToggle: () => void;
  isProgramActive: (id: string) => boolean;
  subPages: { label: string; path: string }[];
  currentPath: string;
}) {
  const location = { pathname: currentPath };
  const hasActiveProgram = programs.some((p) => isProgramActive(p.id));

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left"
        style={
          hasActiveProgram
            ? { backgroundColor: "rgba(0,251,169,0.1)", color: "#171717" }
            : { color: "#A9A9A9" }
        }
      >
        <span style={hasActiveProgram ? { color: "#00FBA9" } : {}}>{icon}</span>
        <span className="flex-1">{label}</span>
        <span
          className="transition-transform"
          style={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
        >
          <ChevronDownIcon />
        </span>
      </button>

      {expanded && (
        <div className="ml-6 mt-1 space-y-0.5">
          {programs.length === 1
            ? // Single program - show sub-pages directly
              subPages.map((subPage) => {
                const programId = programs[0].id;
                const fullPath = subPage.path
                  ? `/programs/${programId}/${subPage.path.replace(/^\//, "")}`
                  : `/programs/${programId}`;
                const isSubPageActive =
                  location.pathname === fullPath ||
                  (subPage.path === "" &&
                    location.pathname === `/programs/${programId}`);
                return (
                  <Link
                    key={subPage.path || "main"}
                    to={fullPath}
                    className="flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors"
                    style={
                      isSubPageActive
                        ? { backgroundColor: "#171717", color: "#FFFFFF" }
                        : { color: "#A9A9A9" }
                    }
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: isSubPageActive
                          ? "#00FBA9"
                          : "#D1D1D1",
                      }}
                    />
                    {subPage.label}
                  </Link>
                );
              })
            : // Multiple programs - show program list
              programs.map((program) => (
                <Link
                  key={program.id}
                  to="/programs/$id"
                  params={{ id: program.id }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors"
                  style={
                    isProgramActive(program.id)
                      ? { backgroundColor: "#171717", color: "#FFFFFF" }
                      : { color: "#A9A9A9" }
                  }
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: isProgramActive(program.id)
                        ? "#00FBA9"
                        : "#D1D1D1",
                    }}
                  />
                  {program.name}
                </Link>
              ))}
        </div>
      )}
    </div>
  );
}

// Icons
function HomeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProductsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M20 7L12 3L4 7M20 7L12 11M20 7V17L12 21M12 11L4 7M12 11V21M4 7V17L12 21"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IntelligenceIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TakebackIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2"
        strokeLinecap="round"
      />
      <path
        d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22"
        strokeLinecap="round"
        strokeDasharray="3 3"
      />
      <path d="M8 12L12 8L16 12" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8V16" strokeLinecap="round" />
    </svg>
  );
}

function TradeInIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M7 10L3 14L7 18" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 14H3" strokeLinecap="round" />
      <path
        d="M17 6L21 10L17 14"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 10H21" strokeLinecap="round" />
    </svg>
  );
}

function ResaleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M15 7C16.1046 7 17 7.89543 17 9M21 9C21 12.3137 18.3137 15 15 15C14.3938 15 13.8087 14.9101 13.2571 14.7429L11 17H9V19H7V21H4C3.44772 21 3 20.5523 3 20V17.4142C3 17.149 3.10536 16.8946 3.29289 16.7071L9.25707 10.7429C9.08989 10.1914 9 9.60617 9 9C9 5.68629 11.6863 3 15 3C18.3137 3 21 5.68629 21 9Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 3V8C13 8.55228 13.4477 9 14 9H19"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9 20L3 17V4L9 7M9 20L15 17M9 20V7M15 17L21 20V7L15 4M15 17V4M9 7L15 4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KioskIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect
        x="4"
        y="2"
        width="16"
        height="16"
        rx="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 22H16" strokeLinecap="round" />
      <path d="M12 18V22" strokeLinecap="round" />
      <circle cx="12" cy="10" r="2" fill="currentColor" />
      <path d="M8 6H16" strokeLinecap="round" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M10.3246 4.31731C10.751 2.5609 13.249 2.5609 13.6754 4.31731C13.9508 5.45193 15.2507 5.99038 16.2478 5.38285C17.7913 4.44239 19.5576 6.2087 18.6172 7.75218C18.0096 8.74925 18.5481 10.0492 19.6827 10.3246C21.4391 10.751 21.4391 13.249 19.6827 13.6754C18.5481 13.9508 18.0096 15.2507 18.6172 16.2478C19.5576 17.7913 17.7913 19.5576 16.2478 18.6172C15.2507 18.0096 13.9508 18.5481 13.6754 19.6827C13.249 21.4391 10.751 21.4391 10.3246 19.6827C10.0492 18.5481 8.74926 18.0096 7.75219 18.6172C6.2087 19.5576 4.44239 17.7913 5.38285 16.2478C5.99038 15.2507 5.45193 13.9508 4.31731 13.6754C2.5609 13.249 2.5609 10.751 4.31731 10.3246C5.45193 10.0492 5.99037 8.74926 5.38285 7.75218C4.44239 6.2087 6.2087 4.44239 7.75219 5.38285C8.74926 5.99037 10.0492 5.45193 10.3246 4.31731Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M12 5V19M5 12H19" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path d="M6 9L12 15L18 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StoreIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M3 9L12 2L21 9V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 21V12H15V21" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TreasuryIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect
        x="3"
        y="8"
        width="18"
        height="12"
        rx="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 8V6C7 4.89543 7.89543 4 9 4H15C16.1046 4 17 4.89543 17 6V8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 12V16" strokeLinecap="round" />
      <circle cx="12" cy="14" r="2" fill="none" />
    </svg>
  );
}

function X402Icon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <polygon
        points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CampaignsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SponsoredIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 10h6M7 14h4" strokeLinecap="round" />
      <circle cx="16" cy="12" r="2" fill="currentColor" />
    </svg>
  );
}

function KnowledgeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShopifyIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M15.5 4.5L14.5 2L12 3L11.5 5L9 5.5L9.5 8L7 9L7.5 12L5.5 13.5L7 16.5L6 19L8.5 20L10.5 22L13 21L15.5 22L17.5 20L20 19L19 16.5L20.5 13.5L18.5 12L19 9L16.5 8L17 5.5L15.5 4.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 8V16M9 12H15" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RecyclingIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M7 19H4.4C4.17909 19 4 18.8209 4 18.6V17M7 19L9 16M7 19L5 16"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 5H19.6C19.8209 5 20 5.17909 20 5.4V7M17 5L15 8M17 5L19 8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 22L12 19M12 22L15 20M12 22L9 20"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 2L12 5M12 2L9 4M12 2L15 4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 9L6.5 12M5 9L2 11M5 9L2 7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 15L17.5 12M19 15L22 13M19 15L22 17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 8L16 16H8L12 8Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarehouseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M3 21V8L12 3L21 8V21"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 21H21" strokeLinecap="round" />
      <path d="M9 21V12H15V21" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 12H7" strokeLinecap="round" />
      <path d="M6 15H7" strokeLinecap="round" />
      <path d="M17 12H18" strokeLinecap="round" />
      <path d="M17 15H18" strokeLinecap="round" />
    </svg>
  );
}

function InventoryIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 21V7C16 5.89543 15.1046 5 14 5H10C8.89543 5 8 5.89543 8 7V21"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 2V5" strokeLinecap="round" />
      <path d="M6 2V5" strokeLinecap="round" />
      <path d="M18 2V5" strokeLinecap="round" />
      <path d="M12 12H12.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PricingIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TradeInReportsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M3 3V21H21" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M7 16L12 11L16 15L21 10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M21 10V3H14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MarketIntelIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12H22" strokeLinecap="round" />
      <path
        d="M12 2C14.5 4.5 16 8 16 12C16 16 14.5 19.5 12 22"
        strokeLinecap="round"
      />
      <path
        d="M12 2C9.5 4.5 8 8 8 12C8 16 9.5 19.5 12 22"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M8 12H8.01M12 12H12.01M16 12H16.01M21 12C21 16.4183 16.9706 20 12 20C10.4607 20 9.01172 19.6565 7.74467 19.0511L3 20L4.39499 16.28C3.51156 15.0423 3 13.5743 3 12C3 7.58172 7.02944 4 12 4C16.9706 4 21 7.58172 21 12Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CustomersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WashRepairIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M3 6H21M3 6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6M3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 11C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11" strokeLinecap="round" />
      <path d="M6 11C6 11 6.5 9 8 9" strokeLinecap="round" />
      <path d="M18 11C18 11 17.5 9 16 9" strokeLinecap="round" />
    </svg>
  );
}
